import {
  buildGroupedRows,
  isGroupHeaderRow,
  resolveGroupedRowsLoadMoreIndex,
  type GroupHeaderRowBase,
} from '@terra/ui-design-system';
import {
  TRACE_RESULT_KEY,
  TraceResultKeySchema,
  isTraceInProgress,
  type TraceGroupByField,
  type TraceGroupCount,
  type TracePreviewDto,
  type TraceResultKey,
} from '@terra/shared-contracts-trace';
import { SignalStatus } from '@terra/shared-schemas-orchestrator';
import type {
  TraceListGrouping,
  TraceListSort,
  TraceListSortOrder,
} from '../../../store/tab/tracesTabScopedFilterStore';
import {
  getExecutionRowId,
  isExecutionsSortableColumn,
} from './executions-list.model';

export const EXECUTIONS_GROUP_HEADER_ID_PREFIX = '__executions_group__:';
export const EXECUTIONS_GROUP_HEADER_ROW_CLASS = 'executions-group-header-row';
export const EXECUTIONS_GROUP_SORTING_ORDER: TraceListSortOrder[] = [
  'asc',
  'desc',
];

const GUARDRAIL_SKIP_ATTACK_REASON_TYPE = 'signal_blocked_by_guardrails';

export type ExecutionsGroupHeaderRow = GroupHeaderRowBase;

export type ExecutionsGridRow = TracePreviewDto | ExecutionsGroupHeaderRow;

export const isExecutionsGroupHeaderRow = (
  row: ExecutionsGridRow | null | undefined,
): row is ExecutionsGroupHeaderRow => isGroupHeaderRow(row);

const resolveResultKey = (
  trace: Pick<
    TracePreviewDto,
    'status' | 'resultKey' | 'signal' | 'skipAttackReasonType'
  >,
): TraceResultKey => {
  if (isTraceInProgress(trace.status)) {
    return TRACE_RESULT_KEY.NONE;
  }

  if (trace.resultKey) {
    return trace.resultKey;
  }

  const status = trace.signal?.status;
  const findingState = trace.signal?.confirmedVulnerabilityFindingState;

  if (status === SignalStatus.CONFIRMED_VULNERABILITY && findingState) {
    return findingState === 'open'
      ? TRACE_RESULT_KEY.CONFIRMED_VULNERABILITY_OPEN
      : TRACE_RESULT_KEY.CONFIRMED_VULNERABILITY_CLOSED;
  }

  const statusResultKey = TraceResultKeySchema.safeParse(status);

  if (statusResultKey.success) {
    return statusResultKey.data;
  }

  return trace.skipAttackReasonType === GUARDRAIL_SKIP_ATTACK_REASON_TYPE
    ? SignalStatus.BLOCKED
    : TRACE_RESULT_KEY.NONE;
};

export const getExecutionsGroupKey = ({
  trace,
  groupByField,
}: {
  trace: TracePreviewDto;
  groupByField: TraceGroupByField;
}): string => {
  switch (groupByField) {
    case 'status':
      return trace.status;
    case 'agent':
      return trace.name;
    case 'result':
      return resolveResultKey(trace);
  }
};

export const isTraceResultGroupKey = (
  groupKey: string,
): groupKey is TraceResultKey =>
  TraceResultKeySchema.safeParse(groupKey).success;

const orderGroupCounts = ({
  groups,
  order,
}: {
  groups: readonly TraceGroupCount[];
  order: TraceListSortOrder;
}): readonly TraceGroupCount[] =>
  order === 'asc' ? groups : [...groups].reverse();

export const buildExecutionsGridRows = ({
  traces,
  grouping,
  groups,
  collapsedGroupKeys,
  getGroupLabel,
}: {
  traces: readonly TracePreviewDto[];
  grouping: TraceListGrouping;
  groups: readonly TraceGroupCount[];
  collapsedGroupKeys: ReadonlySet<string>;
  getGroupLabel: (groupKey: string) => string;
}): ExecutionsGridRow[] => {
  if (!grouping) {
    return [...traces];
  }

  const orderedGroups = orderGroupCounts({ groups, order: grouping.order });
  const countByKey = new Map(
    orderedGroups.map((group) => [group.key, group.count]),
  );

  return buildGroupedRows<TracePreviewDto, ExecutionsGroupHeaderRow>({
    items: traces,
    getItemId: getExecutionRowId,
    getGroupKey: (trace) =>
      getExecutionsGroupKey({ trace, groupByField: grouping.field }),
    getGroupLabel,
    collapsedGroupKeys,
    headerIdPrefix: EXECUTIONS_GROUP_HEADER_ID_PREFIX,
    knownGroupKeys: orderedGroups.map((group) => group.key),
    decorateHeader: (header) => ({
      ...header,
      count: Math.max(countByKey.get(header.groupKey) ?? 0, header.count),
    }),
  });
};

export const resolveExecutionsLoadMoreRowIndex = ({
  rows,
  grouping,
  collapsedGroupKeys,
  hasServerGroups,
  hasMore,
}: {
  rows: readonly ExecutionsGridRow[];
  grouping: TraceListGrouping;
  collapsedGroupKeys: ReadonlySet<string>;
  hasServerGroups: boolean;
  hasMore: boolean;
}): number | null => {
  if (!grouping) {
    return rows.length > 0 ? rows.length - 1 : null;
  }

  return resolveGroupedRowsLoadMoreIndex({
    rows,
    collapsedGroupKeys,
    hasUnseenGroups: !hasServerGroups && hasMore,
  });
};

export type ExecutionsColumnSortState = {
  colId: string;
  sort: TraceListSortOrder;
  sortIndex: number;
};

export const resolveExecutionsColumnSortState = ({
  sort,
  grouping,
}: {
  sort: TraceListSort;
  grouping: TraceListGrouping;
}): ExecutionsColumnSortState[] => {
  const groupState: ExecutionsColumnSortState[] = grouping
    ? [{ colId: grouping.field, sort: grouping.order, sortIndex: 0 }]
    : [];
  const columnState: ExecutionsColumnSortState[] =
    sort && sort.colId !== grouping?.field
      ? [{ colId: sort.colId, sort: sort.sort, sortIndex: groupState.length }]
      : [];

  return [...groupState, ...columnState];
};

export type ExecutionsSortState = {
  sort: TraceListSort;
  grouping: TraceListGrouping;
};

export const isSameExecutionsSortState = ({
  left,
  right,
}: {
  left: ExecutionsSortState;
  right: ExecutionsSortState;
}): boolean =>
  left.sort?.colId === right.sort?.colId &&
  left.sort?.sort === right.sort?.sort &&
  left.grouping?.field === right.grouping?.field &&
  left.grouping?.order === right.grouping?.order;

export const resolveExecutionsSortChange = ({
  sortedColumn,
  sort,
  grouping,
}: {
  sortedColumn: { colId: string; sort: TraceListSortOrder } | null;
  sort: TraceListSort;
  grouping: TraceListGrouping;
}): ExecutionsSortState => {
  if (grouping && sortedColumn?.colId === grouping.field) {
    return { sort, grouping: { ...grouping, order: sortedColumn.sort } };
  }

  return {
    sort:
      sortedColumn && isExecutionsSortableColumn(sortedColumn.colId)
        ? { colId: sortedColumn.colId, sort: sortedColumn.sort }
        : null,
    grouping,
  };
};
